import { useCallback } from "react";
import { Deco, DecoType } from "../components/Deco";
import { LinkWrapper } from "../components/linkWrapper";
import { StrongLink } from "../components/StrongLink";
import { KnowingType } from "../components/Wizard/wizard.types";
import { Flex } from "../layouts/Flex";
import { Spacing } from "../layouts/Spacing";
import { ExpertiseCard } from "./Expertises";
import styles from "./WizardResults.module.css";
("../components/Deco");

export type WizardResultsProps = {
  title: string;
  mainImage: URL;
  expertises: any;
  finder: any;
  financials: any;
  advisor: any;
  knowledge: any;
  query: any;
  results: any;
  notFound: boolean;
};

const Step = ({
  userType,
  know,
  dontknow,
  children,
  alt,
  deco,
}: {
  children: any;
  userType: KnowingType;
  know: {
    title: string;
    text: string;
  };
  dontknow: {
    title: string;
    text: string;
  };
  alt?: {
    title: string;
    text: string;
  };
  deco?: DecoType;
}) => {
  const isKnowing = userType === KnowingType.Know;
  let content = isKnowing ? know : dontknow;
  return (
    <div className={styles.step}>
      {deco ? (
        <h3 className={styles.stepTitle}>
          <Deco deco={deco}>
            {content?.title}
          </Deco>
        </h3>
      ) : (
        <h3 className={styles.stepTitle}>{content?.title}</h3>
      )}
      {alt && <p className={styles.stepText}>{alt.text}</p>}
      <p className={styles.stepText}>{content?.text}</p>
      {children}
    </div>
  );
};

const checkForMatchingLinks = (queryXps: any, links: any) => {
  let xps = links.map(({ expertises }: { expertises: any }) => expertises)[0];
  return xps?.some((x: any) => queryXps.includes(x));
};

const LinksList = ({ links }: { links: any }) => {
  return (
    <ul className={styles.linkList}>
      {links.map((item: any) => (
        <li key={item._key} className={styles.linkItem}>
          <LinkWrapper
            url={item.url}
            addIcon={true}
            className={styles.linkTitle}
          >
            {item.title}
          </LinkWrapper>
          {item.text && <p className={styles.linkText}>{item.text}</p>}
        </li>
      ))}
    </ul>
  );
};

export const WizardResults = ({
  query,
  expertises,
  finder,
  financials,
  advisor,
  knowledge,
  results,
  notFound,
}: WizardResultsProps) => {
  let expertisesList = expertises.list.filter((item: any) =>
    query.expertises.includes(item.title)
  );
  const isKnow = query.t === KnowingType.Know;

  const getCoursesLink = useCallback(() => {
    let coursesLink = "opleidingen";
    coursesLink += "?vakgebied=" + query.expertises.join(",")?.toLowerCase();
    coursesLink += "&niveau=";
    coursesLink +=
      decodeURIComponent(query.l) === "nopref"
        ? ""
        : decodeURIComponent(query.l)?.toLowerCase();
    if (!notFound) {
      coursesLink += "&duur=";
      coursesLink +=
        decodeURIComponent(query.d) === "nopref"
          ? ""
          : decodeURIComponent(query.d)?.toLowerCase();
      coursesLink += "&type=";
      coursesLink +=
        decodeURIComponent(query.s) === "nopref"
          ? ""
          : decodeURIComponent(query.s);
    }
    coursesLink = coursesLink.replaceAll(/,\s/g, ",");

    return coursesLink;
  }, [notFound, query.d, query.expertises, query.l, query.s]);

  return (
    <div className={styles.wizard}>
      <Spacing size="sm">
        {!isKnow && expertisesList.length > 0 && (
          <Step {...expertises} userType={query.t}>
            <Flex items={2}>
              {expertisesList.map((item: any) => (
                <ExpertiseCard key={item.title} {...item} />
              ))}
            </Flex>
          </Step>
        )}
        <Step
          {...finder}
          userType={query.t}
          alt={notFound ? { text: finder.nohits.text } : undefined}
          deco={!isKnow ? "crooked-arrow" : undefined}
        >
          {results.length > 0 && (
            <ul className={styles.linkList}>
              {results.slice(0, 3).map((item: any, index: any) => (
                <li key={item.naam} className={styles.linkItem}>
                  <LinkWrapper
                    url={item.url}
                    addIcon={true}
                    className={styles.linkTitle}
                  >
                    {item.naam}
                  </LinkWrapper>
                  <p className={styles.linkText}>{item.opleider}</p>
                </li>
              ))}
            </ul>
          )}
          <StrongLink
            text={
              isKnow
                ? "Bekijk alle opleidingen die interessant zijn"
                : "Bekijk alle opleidingen die bij jou passen"
            }
            url={getCoursesLink()}
          />
        </Step>
        {isKnow && (
          <Step {...financials} userType={query.t} deco={"dot"}>
            <LinksList links={financials.links} />
          </Step>
        )}
        <Step {...advisor} userType={query.t} deco={"left-arrow"}>
          <LinksList links={advisor.links} />
        </Step>
        {!isKnow && (
          <Step {...knowledge} userType={query.t} deco={"dot"}>
            <LinksList links={knowledge.links} />
          </Step>
        )}
      </Spacing>
    </div>
  );
};
